import {
	type UseMutationOptions,
	useMutation,
	useQueryClient
} from '@tanstack/react-query'
import type { Experience } from '@/features/experience-shell/types.ts'
import {
	type HttpUpdateExperienceExpertiseStatus,
	httpUpdateExperienceExpertiseStatus
} from '@/features/experience-vetting/http/experience-vetting.http.ts'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS } from '@/query-client'

export const useUpdateExperienceExpertiseStatus = (
	{
		experienceId,
		expertiseId
	}: { experienceId: number; expertiseId: number },
	options?: UseMutationOptions<
		unknown,
		unknown,
		HttpUpdateExperienceExpertiseStatus['payload']
	>
) => {
	const queryClient = useQueryClient()
	return useMutation({
		...options,
		mutationFn: (payload) =>
			httpUpdateExperienceExpertiseStatus({
				urlParams: { experienceId, expertiseId },
				payload,
				params: {
					response_type: 'small'
				}
			}),
		onError: (error, variables, onMutateResult, context) => {
			queryClient.setQueriesData(
				{ queryKey: [QUERY_KEYS.EXPERIENCE_VETTING, experienceId] },
				context
			)
			errorHandler(error)
			options?.onError?.(error, variables, onMutateResult, context)
		},
		onMutate: async (variables) => {
			const queryKey = [QUERY_KEYS.EXPERIENCE_VETTING, experienceId]

			// Cancel any outgoing refetches (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey })

			// Snapshot previous values
			const previousData = queryClient.getQueryData<Experience>(queryKey)

			// Update cached data
			if (previousData) {
				queryClient.setQueriesData<Experience>(
					{ queryKey },
					{
						...previousData,
						expertise: {
							...previousData.expertise,
							status: variables.status
						}
					}
				)
			}
			return previousData
		}
	})
}
